import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { langField } from "../lib/i18n";

export const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, updateQty, removeItem, subtotal, t, lang, formatPrice } = useApp();
  const navigate = useNavigate();

  if (!cartOpen) return null;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => setCartOpen(false)}
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-kult-black border-l-2 border-kult-pink flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-kult-border">
          <h2 className="font-display text-3xl">{t.cart.title}</h2>
          <button
            onClick={() => setCartOpen(false)}
            data-testid="cart-close"
            aria-label="Close cart"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {cart.length === 0 && (
            <div className="text-center py-20">
              <p className="font-mono text-sm tracking-widest text-kult-muted">{t.cart.empty}</p>
            </div>
          )}
          {cart.map((item, idx) => (
            <div
              key={idx}
              className="flex gap-3 border border-kult-border p-3"
              data-testid={`cart-item-${idx}`}
            >
              {item.image && (
                <img src={item.image} alt="" className="w-20 h-24 object-cover" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-display text-sm leading-tight">
                  {langField(item, "name", lang)}
                </p>
                <p className="font-mono text-[11px] text-kult-muted mt-1">
                  {item.size && `${item.size}`}{item.color ? ` / ${item.color}` : ""}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-kult-border">
                    <button
                      onClick={() => updateQty(idx, item.quantity - 1)}
                      className="p-1.5 hover:bg-kult-pink hover:text-black"
                      data-testid={`cart-decrement-${idx}`}
                    >
                      <Minus size={12} />
                    </button>
                    <span className="px-3 font-mono text-xs">{item.quantity}</span>
                    <button
                      onClick={() => updateQty(idx, item.quantity + 1)}
                      className="p-1.5 hover:bg-kult-pink hover:text-black"
                      data-testid={`cart-increment-${idx}`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <span className="font-mono text-sm bg-kult-yellow text-black px-2 py-0.5">
                    {formatPrice(item.price * item.quantity, { decimals: 0 })}
                  </span>
                </div>
              </div>
              <button
                onClick={() => removeItem(idx)}
                className="self-start text-kult-muted hover:text-kult-pink"
                data-testid={`cart-remove-${idx}`}
                aria-label="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-kult-border p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs tracking-[0.25em]">{t.cart.subtotal}</span>
            <span
              className="font-display text-2xl bg-kult-yellow text-black px-2"
              data-testid="cart-subtotal"
            >
              {formatPrice(subtotal)}
            </span>
          </div>
          <button
            disabled={cart.length === 0}
            onClick={() => {
              setCartOpen(false);
              navigate("/checkout");
            }}
            className="w-full bg-kult-pink text-black font-display text-xl py-4 hover:bg-kult-yellow transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cart-checkout"
          >
            {t.cart.checkout}
          </button>
          <button
            onClick={() => setCartOpen(false)}
            className="w-full border border-kult-border font-mono text-xs tracking-[0.25em] py-3 hover:border-kult-pink"
            data-testid="cart-continue"
          >
            {t.cart.continue}
          </button>
        </div>
      </aside>
    </div>
  );
};
